class BackgroundMusicService {
  private audioContext: AudioContext | null = null;
  private currentAudio: HTMLAudioElement | null = null;
  private isPlaying = false;
  private currentMusic = '';
  private isInitializing = false;

  constructor() {
    this.initializeAudioContext();
  }

  private initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.error('AudioContext 초기화 실패:', error);
    }
  }

  async playBackgroundMusic(musicType: string): Promise<void> {
    try {
      console.log(`배경음악 재생 요청: ${musicType}, 현재 재생 중: ${this.currentMusic}, 초기화 중: ${this.isInitializing}`);
      
      // 이미 초기화 중이면 무시
      if (this.isInitializing) {
        console.log('이미 초기화 중입니다.');
        return;
      }
      
      // 이미 같은 음악이 재생 중이면 무시
      if (this.isPlaying && this.currentMusic === musicType) {
        console.log(`이미 ${musicType}이 재생 중입니다.`);
        return;
      }

      this.isInitializing = true;
      
      // 이전 음악 정지
      await this.stopBackgroundMusic();

      // 음악 타입에 따른 파일 경로 설정
      const musicFiles: { [key: string]: string } = {
        '배경음 1': '/sounds/bg_music_1.mp3',
        '배경음 2': '/sounds/bg_music_2.mp3',
        '배경음 3': '/sounds/bg_music_3.mp3',
        '배경음 4': '/sounds/bg_music_4.mp3',
        '배경음 5': '/sounds/bg_music_5.mp3',
        '배경음 6': '/sounds/bg_music_6.mp3',
        '명상음': '/sounds/bg_meditation.mp3',
      };

      const musicFile = musicFiles[musicType] || musicFiles['배경음 1'];
      
      // 새로운 오디오 요소 생성
      this.currentAudio = new Audio(musicFile);
      this.currentAudio.loop = true;
      this.currentAudio.volume = 0.3; // 볼륨 설정

      // 오디오 로드 완료 후 재생
      this.currentAudio.addEventListener('canplaythrough', () => {
        if (this.currentAudio && !this.isPlaying) {
          this.currentAudio.play().then(() => {
            this.isPlaying = true;
            this.currentMusic = musicType;
            this.isInitializing = false;
            console.log(`배경음악 재생 시작: ${musicType}`);
          }).catch((error) => {
            console.error('배경음악 재생 실패:', error);
            this.isPlaying = false;
            this.currentMusic = '';
            this.isInitializing = false;
          });
        }
      });

      // 오디오 로드 실패 시 처리
      this.currentAudio.addEventListener('error', (error) => {
        console.error('배경음악 로드 실패:', error);
        this.isInitializing = false;
        // 로드 실패 시 기본 사운드 생성
        this.createDefaultBackgroundSound();
      });

    } catch (error) {
      console.error('배경음악 재생 중 오류:', error);
      this.isInitializing = false;
      // 오류 발생 시 기본 사운드 생성
      this.createDefaultBackgroundSound();
    }
  }

  private createDefaultBackgroundSound(): void {
    if (!this.audioContext) return;

    try {
      // 기본 배경음악을 Web Audio API로 생성
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // 부드러운 사인파 생성
      oscillator.frequency.setValueAtTime(220, this.audioContext.currentTime); // A3
      oscillator.type = 'sine';
      
      // 페이드 인 효과
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, this.audioContext.currentTime + 2);
      
      oscillator.start(this.audioContext.currentTime);
      
      this.isPlaying = true;
      this.currentMusic = '기본 배경음';
      console.log('기본 배경음악 재생 시작');
      
    } catch (error) {
      console.error('기본 배경음악 생성 실패:', error);
    }
  }

  async stopBackgroundMusic(): Promise<void> {
    try {
      console.log(`BackgroundMusicService: 배경음악 정지 요청, 현재 재생 중: ${this.currentMusic}`);
      
      if (this.currentAudio) {
        // 페이드 아웃 효과
        if (this.currentAudio.volume > 0) {
          const fadeOutDuration = 1000; // 1초
          const fadeOutSteps = 20;
          const volumeStep = this.currentAudio.volume / fadeOutSteps;
          const stepDuration = fadeOutDuration / fadeOutSteps;

          for (let i = 0; i < fadeOutSteps; i++) {
            setTimeout(() => {
              if (this.currentAudio) {
                this.currentAudio.volume = Math.max(0, this.currentAudio.volume - volumeStep);
              }
            }, i * stepDuration);
          }

          // 페이드 아웃 완료 후 정지
          setTimeout(() => {
            if (this.currentAudio) {
              this.currentAudio.pause();
              this.currentAudio.currentTime = 0;
            }
          }, fadeOutDuration);
        } else {
          this.currentAudio.pause();
          this.currentAudio.currentTime = 0;
        }
        
        this.currentAudio = null;
      }

      this.isPlaying = false;
      this.currentMusic = '';
      this.isInitializing = false;
      console.log('BackgroundMusicService: 배경음악 정지 완료');
      
    } catch (error) {
      console.error('BackgroundMusicService: 배경음악 정지 실패:', error);
      this.isInitializing = false;
    }
  }

  isBackgroundMusicPlaying(): boolean {
    return this.isPlaying;
  }

  getCurrentMusic(): string {
    return this.currentMusic;
  }

  setVolume(volume: number): void {
    if (this.currentAudio) {
      this.currentAudio.volume = Math.max(0, Math.min(1, volume));
    }
  }
}

export default BackgroundMusicService; 